import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FaCloudUploadAlt, FaPlusCircle } from 'react-icons/fa';
import { RiDeleteBin6Fill, RiDeleteBinLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { isValidEmail } from '../../utils/utils';
import ConfirmDialog from '../../components/ConfirmDialog';
import HelpIcon from '../InvestmentScreening/HelpIcon';

import { InputLimits } from '../../contexts/constants';
type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const SETTING_KEY = 'inquireToAddress';

/** 問い合わせ種類（テンプレート適用対象） */
type InquiryType = 'bug' | 'request' | 'other';

/** 添付ファイル1件分の管理用型 */
interface AttachedFile {
  file: File;
  fileName: string;
  size: number; // バイト数（元データ、Base64変換前）
}

/** 問い合わせ種類ごとのテンプレート・記入例定義（i18nキーで管理） */
interface InquiryTemplateDef {
  type: InquiryType;
  labelKey: string;
  templateKey: string;
  exampleKey: string;
}

// 許可する拡張子（画像 + PDF + Office文書 + zip）
const ALLOWED_ATTACHMENT_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif',
  'pdf',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'zip',
];
// 許可するMIMEタイプ（拡張子とセットで二重チェックする）
const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip', 'application/x-zip-compressed',
];
// 添付ファイル1件あたりの最大サイズ（元データ、Base64変換前）
const MAX_ATTACHMENT_FILE_SIZE = 3 * 1024 * 1024; // 3MB
// 添付ファイル合計の最大サイズ（元データ、Base64変換前）
const MAX_ATTACHMENT_TOTAL_SIZE = 4 * 1024 * 1024; // 4MB
// 添付ファイルの最大枚数
const MAX_ATTACHMENT_COUNT = 5;

// 問い合わせ種類定義（テンプレート・記入例は i18n から取得）
const INQUIRY_TEMPLATES: InquiryTemplateDef[] = [
  { type: 'bug', labelKey: 'components.InquireModal.inquiryType.bug', templateKey: 'components.InquireModal.template.bug', exampleKey: 'components.InquireModal.example.bug' },
  { type: 'request', labelKey: 'components.InquireModal.inquiryType.request', templateKey: 'components.InquireModal.template.request', exampleKey: 'components.InquireModal.example.request' },
  { type: 'other', labelKey: 'components.InquireModal.inquiryType.other', templateKey: '', exampleKey: 'components.InquireModal.example.other' },
];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
};

const getExtension = (fileName: string): string => {
  const idx = fileName.lastIndexOf('.');
  if (idx < 0) return '';
  return fileName.slice(idx + 1).toLowerCase();
};

const InquireModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const prevAttachedCountRef = useRef(0);
  const prevAttachmentErrorRef = useRef('');

  const [toAddress, setToAddress] = useState('');
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccEmailErrors, setCcEmailErrors] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  // 問い合わせ種類・テンプレート関連
  const [inquiryType, setInquiryType] = useState<InquiryType | ''>('');
  const [pendingInquiryType, setPendingInquiryType] = useState<InquiryType | ''>('');
  const {
    isOpen: isOverwriteConfirmOpen,
    onOpen: onOverwriteConfirmOpen,
    onClose: onOverwriteConfirmClose,
  } = useDisclosure();

  // 添付ファイル関連
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // モーダルが開くたびに宛先を取得し、フォームをリセット
  useEffect(() => {
    if (!isOpen) return;
    setBody('');
    setBodyError('');
    setCcEmails([]);
    setCcEmailErrors([]);
    setInquiryType('');
    setPendingInquiryType('');
    setAttachedFiles([]);
    setAttachmentError('');
    setIsDragging(false);
    fetchToAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchToAddress = async () => {
    try {
      const response = await WebAPI.request({
        query: `
          query getSettingValueByKey($settingKey: String, $hasDeleted: Boolean) {
            getSettingValueByKey(settingKey: $settingKey, hasDeleted: $hasDeleted) {
              settingValue
            }
          }`,
        variables: { settingKey: SETTING_KEY, hasDeleted: false },
      });
      setToAddress(response?.data?.getSettingValueByKey?.settingValue ?? '');
    } catch (err) {
      console.error('fetchToAddress failed:', err);
      setToAddress('');
    }
  };

  const handleClose = useCallback(() => {
    setBody('');
    setBodyError('');
    setCcEmails([]);
    setCcEmailErrors([]);
    setToAddress('');
    setInquiryType('');
    setPendingInquiryType('');
    setAttachedFiles([]);
    setAttachmentError('');
    setIsDragging(false);
    onClose();
  }, [onClose]);

  const applyTemplate = useCallback((type: InquiryType) => {
    const def = INQUIRY_TEMPLATES.find(d => d.type === type);
    if (!def) return;
    setInquiryType(type);
    setBody(t(def.templateKey));
    setBodyError('');
  }, [t]);

  const handleInquiryTypeChange = (value: string) => {
    const newType = value as InquiryType | '';
    if (!newType) {
      setInquiryType('');
      return;
    }
    if (!body.trim()) {
      applyTemplate(newType);
    } else {
      setPendingInquiryType(newType);
      onOverwriteConfirmOpen();
    }
  };

  const handleOverwriteConfirm = () => {
    if (pendingInquiryType) {
      applyTemplate(pendingInquiryType);
    }
    setPendingInquiryType('');
  };

  const handleOverwriteCancel = () => {
    setPendingInquiryType('');
    onOverwriteConfirmClose();
  };

  const validateAndAddAttachments = useCallback((files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const newFiles: AttachedFile[] = [];
    let errorMessage = '';
    let duplicateFileName = '';

    const existingNames = new Set(attachedFiles.map(f => f.fileName));
    const existingTotalSize = attachedFiles.reduce((sum, f) => sum + f.size, 0);
    let runningTotalSize = existingTotalSize;
    let runningCount = attachedFiles.length;

    for (const file of Array.from(files)) {
      // 重複ファイル名チェック
      if (existingNames.has(file.name) || newFiles.some(f => f.fileName === file.name)) {
        duplicateFileName = file.name;
        continue;
      }
      // 拡張子チェック
      const ext = getExtension(file.name);
      if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext) || !ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type)) {
        errorMessage = t('components.InquireModal.validation.attachmentExtension');
        continue;
      }
      // 1件あたりサイズチェック
      if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
        errorMessage = t('components.InquireModal.validation.attachmentSize');
        continue;
      }
      // 件数チェック
      if (runningCount + 1 > MAX_ATTACHMENT_COUNT) {
        errorMessage = t('components.InquireModal.validation.attachmentCount');
        continue;
      }
      // 合計サイズチェック
      if (runningTotalSize + file.size > MAX_ATTACHMENT_TOTAL_SIZE) {
        errorMessage = t('components.InquireModal.validation.attachmentTotalSize');
        continue;
      }

      newFiles.push({ file, fileName: file.name, size: file.size });
      existingNames.add(file.name);
      runningTotalSize += file.size;
      runningCount += 1;
    }

    if (newFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }
    setAttachmentError(errorMessage);
    if (duplicateFileName) {
      toast({
        title: t('components.InquireModal.title'),
        description: t('components.InquireModal.message.duplicateFile', { fileName: duplicateFileName }),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [attachedFiles, t, toast]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndAddAttachments(e.target.files);
    e.target.value = '';
  };

  // ドラッグ＆ドロップ用ハンドラー
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSending) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isSending) return;
    validateAndAddAttachments(e.dataTransfer.files);
  };

  // クリップボード貼り付け（Ctrl + V）ハンドラー
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (isSending) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedFiles: File[] = [];
    let hasFile = false;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          hasFile = true;
          // クリップボード画像の自動リネーム（重複防止用タイムスタンプ付与）
          let fileName = file.name;
          if (!fileName || fileName === 'image.png' || fileName === 'blob') {
            const now = new Date();
            const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
            const ext = file.type.split('/')[1] || 'png';
            fileName = `clipboard_${timestamp}.${ext}`;
          }
          const renamedFile = new File([file], fileName, { type: file.type });
          pastedFiles.push(renamedFile);
        }
      }
    }

    // クリップボードにファイル/画像が含まれている場合のみ添付処理を実施（通常のテキストペーストは通す）
    if (hasFile) {
      e.preventDefault();
      validateAndAddAttachments(pastedFiles);
    }
  }, [isSending, validateAndAddAttachments]);

  const handleRemoveAttachment = (idx: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
    setAttachmentError('');
  };

  // 添付ファイルが増えた／添付エラーが新たに発生したタイミングでモーダル本文を最下部までスクロール
  useEffect(() => {
    const countIncreased = attachedFiles.length > prevAttachedCountRef.current;
    const errorAppeared = !!attachmentError && attachmentError !== prevAttachmentErrorRef.current;
    if (countIncreased || errorAppeared) {
      const el = modalBodyRef.current;
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    }
    prevAttachedCountRef.current = attachedFiles.length;
    prevAttachmentErrorRef.current = attachmentError;
  }, [attachedFiles.length, attachmentError]);

  const validate = (): boolean => {
    let isValid = true;

    // Cc メールアドレス形式チェック
    const newCcErrors = ccEmails.map(email => {
      const trimmed = email.trim();
      if (trimmed && !isValidEmail(trimmed)) {
        return t('components.InquireModal.validation.ccEmailInvalid');
      }
      return '';
    });
    setCcEmailErrors(newCcErrors);
    if (newCcErrors.some(e => e !== '')) {
      isValid = false;
    }

    // 本文チェック
    if (!body.trim()) {
      setBodyError(t('components.InquireModal.validation.bodyRequired'));
      isValid = false;
    } else if (body.length > InputLimits.INQUIRY_BODY) {
      setBodyError(t('components.InquireModal.validation.bodyTooLong'));
      isValid = false;
    } else {
      setBodyError('');
    }

    return isValid;
  };

  const handleSend = async () => {
    if (!validate()) return;
    WebAPI.setLoading(true);
    setIsSending(true);
    try {
      const attachments = await Promise.all(
        attachedFiles.map(async af => {
          // 1. presigned PUT URLを取得
          const urlRes = await WebAPI.request({
            query: `
              query getInquiryAttachmentUploadUrl($fileName: String!, $contentType: String!, $fileSize: Int!) {
                getInquiryAttachmentUploadUrl(fileName: $fileName, contentType: $contentType, fileSize: $fileSize) {
                  key
                  uploadUrl
                }
              }`,
            variables: { fileName: af.fileName, contentType: af.file.type, fileSize: af.size },
          });
          const { key, uploadUrl } = urlRes.data.getInquiryAttachmentUploadUrl;

          // 2. S3へ直接アップロード（GraphQL/Lambdaを経由しない）
          const putRes = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': af.file.type },
            body: af.file,
          });
          if (!putRes.ok) {
            throw new Error(`S3 upload failed: ${putRes.status}`);
          }

          return { fileName: af.fileName, contentType: af.file.type, key };
        }),
      );

      const response = await WebAPI.request({
        query: `
          mutation sendInquiryMail($input: SendInquiryMailInput!) {
            sendInquiryMail(input: $input) { status }
          }`,
        variables: {
          input: {
            subject: t('common.inquire.subject'),
            body,
            ccEmployeeIds: ccEmails.map(e => e.trim()).filter(e => e.length > 0),
            attachments,
          },
        },
      });
      const status = response?.data?.sendInquiryMail?.status;
      if (status === 'success') {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.sendSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        handleClose();
      } else if (status === 'noRecipient') {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.noRecipient'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else if (status === 'attachmentTooLarge') {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.attachmentTooLarge'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (status === 'invalidAttachment') {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.invalidAttachment'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (status === 'malwareDetected') {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.malwareDetected'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (status === 'attachmentScanTimeout') {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.attachmentScanTimeout'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('components.InquireModal.title'),
          description: t('components.InquireModal.message.sendError'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('sendInquiryMail failed:', err);
      toast({
        title: t('components.InquireModal.title'),
        description: t('components.InquireModal.message.sendError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      WebAPI.setLoading(false);
      setIsSending(false);
    }
  };

  const isToEmpty = !toAddress || toAddress.trim() === '';
  const currentExampleDef = INQUIRY_TEMPLATES.find(d => d.type === inquiryType);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      closeOnEsc={!isSending}
      closeOnOverlayClick={!isSending}
    >
      <ModalOverlay />
      <ModalContent onPaste={handlePaste} maxH="90vh">
        <ModalHeader>{t('components.InquireModal.title')}</ModalHeader>
        <ModalCloseButton isDisabled={isSending} />
        <ModalBody ref={modalBodyRef} overflowY="auto">
          <VStack align="stretch" spacing={4}>
            {/* 宛先（To） */}
            <Box>
              <FormLabel fontWeight="bold" mb={1}>
                {t('components.InquireModal.label.to')}
              </FormLabel>
              <Text fontSize="sm" color="gray.700">
                {t('components.InquireModal.toAddressDisplayName')}
              </Text>
            </Box>

            {/* Cc */}
            <Box>
              <FormLabel fontWeight="bold" mb={1}>
                {t('components.InquireModal.label.cc')}
              </FormLabel>
              <VStack align="stretch" spacing={2}>
                {ccEmails.map((email, idx) => (
                  <FormControl key={idx} isInvalid={!!ccEmailErrors[idx]}>
                    <InputGroup>
                      <Input
                        value={email}
                        onChange={e => {
                          setCcEmails(prev => prev.map((v, i) => i === idx ? e.target.value : v));
                          setCcEmailErrors(prev => prev.map((v, i) => i === idx ? '' : v));
                        }}
                        placeholder={t('components.InquireModal.placeholder.ccEmail')}
                        bgColor="white"
                        pr="40px"
                        isInvalid={!!ccEmailErrors[idx]}
                      />
                      <InputRightElement height="100%">
                        <IconButton
                          aria-label="Remove Cc"
                          icon={<RiDeleteBin6Fill />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => {
                            setCcEmails(prev => prev.filter((_, i) => i !== idx));
                            setCcEmailErrors(prev => prev.filter((_, i) => i !== idx));
                          }}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{ccEmailErrors[idx]}</FormErrorMessage>
                  </FormControl>
                ))}
              </VStack>
              <IconButton
                aria-label="Add Cc"
                icon={<FaPlusCircle />}
                onClick={() => {
                  setCcEmails(prev => [...prev, '']);
                  setCcEmailErrors(prev => [...prev, '']);
                }}
                colorScheme="blue"
                size="sm"
                mt={ccEmails.length > 0 ? 2 : 0}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {t('components.InquireModal.note.ccRestriction')}
              </Text>
            </Box>

            {/* 件名（読み取り専用） */}
            <Box>
              <FormLabel fontWeight="bold" mb={1}>
                {t('components.InquireModal.label.subject')}
              </FormLabel>
              <Text fontSize="sm" color="gray.700">
                {t('common.inquire.subject')}
              </Text>
            </Box>

            {/* 問い合わせ種類 */}
            <FormControl>
              <FormLabel fontWeight="bold" mb={1}>
                {t('components.InquireModal.label.inquiryType')}
              </FormLabel>
              <Select
                value={inquiryType}
                onChange={e => handleInquiryTypeChange(e.target.value)}
                bgColor="white"
                isDisabled={isSending}
              >
                <option value="">{t('components.InquireModal.placeholder.inquiryType')}</option>
                {INQUIRY_TEMPLATES.map(def => (
                  <option key={def.type} value={def.type}>{t(def.labelKey)}</option>
                ))}
              </Select>
            </FormControl>

            {/* 書き方の例 */}
            {currentExampleDef && (
              <Box bgColor="gray.50" borderRadius="md" p={3}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>
                  {t('components.InquireModal.exampleLabel')}
                </Text>
                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                  {t(currentExampleDef.exampleKey)}
                </Text>
              </Box>
            )}

            {/* 本文 */}
            <FormControl isInvalid={!!bodyError}>
              <FormLabel fontWeight="bold">
                {t('components.InquireModal.label.body')}
              </FormLabel>
              <Textarea
                value={body}
                onChange={e => {
                  setBody(e.target.value);
                  setBodyError('');
                }}
                placeholder={t('components.InquireModal.placeholder.body')}
                rows={8}
                maxLength={InputLimits.INQUIRY_BODY}
                bgColor="white"
              />
              <FormErrorMessage>{bodyError}</FormErrorMessage>
            </FormControl>

            {/* 添付ファイル（ドロップゾーン＆ペースト対応） */}
            <FormControl isInvalid={!!attachmentError}>
              <HStack spacing={1} mb={1}>
                <FormLabel fontWeight="bold" mb={0}>
                  {t('components.InquireModal.label.attachment')}
                </FormLabel>
                <HelpIcon size="18px" placement="top-start" width="360px">
                  <Text whiteSpace="pre-wrap" fontSize="sm">
                    {t('components.InquireModal.note.attachmentRestriction')}
                  </Text>
                </HelpIcon>
              </HStack>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_ATTACHMENT_EXTENSIONS.map(ext => `.${ext}`).join(',')}
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />

              {/* ドロップゾーン */}
              <Box
                role="button"
                tabIndex={isSending ? -1 : 0}
                aria-label={t('components.InquireModal.button.selectFile')}
                border="2px dashed"
                borderColor={isDragging ? 'blue.400' : 'gray.300'}
                bgColor={isDragging ? 'blue.50' : 'gray.50'}
                borderRadius="md"
                p={4}
                textAlign="center"
                cursor={isSending ? 'not-allowed' : 'pointer'}
                onClick={() => {
                  if (!isSending) fileInputRef.current?.click();
                }}
                onKeyDown={(e) => {
                  if (isSending) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                transition="all 0.2s"
                _hover={!isSending ? {
                  borderColor: 'blue.400',
                  bgColor: 'blue.50',
                  '.inquire-select-file-btn': { borderColor: 'blue.400', bgColor: 'blue.50' },
                } : undefined}
                _focusVisible={{ boxShadow: 'outline' }}
              >
                <VStack spacing={2}>
                  <Icon as={FaCloudUploadAlt} boxSize={8} color={isDragging ? 'blue.500' : 'gray.400'} />
                  <Text fontSize="xs" color="gray.600">
                    {t('components.InquireModal.note.dragDropPaste')}
                  </Text>
                  <Button
                    as="span"
                    className="inquire-select-file-btn"
                    isDisabled={isSending}
                    size="sm"
                    bgColor="white"
                    border="1px solid"
                    borderColor="gray.300"
                    pointerEvents="none"
                    _hover={{ bgColor: 'white' }}
                  >
                    {t('components.InquireModal.button.selectFile')}
                  </Button>
                </VStack>
              </Box>

              {/* 添付ファイル一覧 */}
              {attachedFiles.length > 0 && (
                <VStack align="stretch" spacing={1} mt={2}>
                  {attachedFiles.map((af, idx) => (
                    <HStack key={`${af.fileName}-${idx}`} justify="space-between" bgColor="gray.50" px={2} py={1} borderRadius="md">
                      <Text fontSize="sm" isTruncated maxW="70%">
                        {af.fileName}
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500">
                          {formatFileSize(af.size)}
                        </Text>
                        <IconButton
                          aria-label="Remove attachment"
                          icon={<RiDeleteBinLine />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          isDisabled={isSending}
                          onClick={() => handleRemoveAttachment(idx)}
                        />
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              )}
              <FormErrorMessage>{attachmentError}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            isLoading={isSending}
            isDisabled={isSending || isToEmpty}
            onClick={(e) => { e.currentTarget.blur(); handleSend(); }}
          >
            {t('common.button.send')}
          </Button>
          <Button variant="ghost" onClick={handleClose} isDisabled={isSending}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>

      <ConfirmDialog
        isOpen={isOverwriteConfirmOpen}
        title={t('components.InquireModal.overwriteConfirm.title')}
        message={t('components.InquireModal.overwriteConfirm.message')}
        confirmColorScheme="blue"
        onConfirm={handleOverwriteConfirm}
        onClose={handleOverwriteCancel}
      />
    </Modal>
  );
};

export default InquireModal;
